import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AppNav from '../AppNav/AppNav';

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function highlightRefs(claimText, terms) {
  let html = escapeHtml(claimText);
  terms.forEach((term) => {
    ['前記', '該'].forEach((prefix) => {
      const pattern = escapeHtml(prefix + term);
      html = html.replaceAll(
        pattern,
        `<mark class="patent-tool__mark patent-tool__mark--ref">${escapeHtml(prefix)}<strong>${escapeHtml(term)}</strong></mark>`,
      );
    });
  });
  return html;
}

function highlightSnippet(snippet, termStart, termLen) {
  const before = escapeHtml(snippet.slice(0, termStart));
  const match = escapeHtml(snippet.slice(termStart, termStart + termLen));
  const after = escapeHtml(snippet.slice(termStart + termLen));
  return `${before}<mark class="patent-tool__mark patent-tool__mark--found"><strong>${match}</strong></mark>${after}`;
}

export default function AntecedentCheckerPage() {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function runCheck() {
    if (!text.trim()) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/++api++/@antecedent-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ text }),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        throw new Error(data.error || `Request failed with status ${response.status}`);
      }
      setResult(data);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }

  const claims = result?.claims || [];
  const totalRefs = claims.reduce((count, claim) => count + claim.refs.length, 0);
  const missing = claims.reduce(
    (count, claim) => count + claim.refs.filter((ref) => !ref.has_basis).length,
    0,
  );

  return (
    <main className="patent-tool">
      <section className="patent-tool__shell">
        <AppNav currentPath="/tools" />
        <header className="patent-tool__hero">
          <p className="patent-tool__eyebrow">Claim Check</p>
          <h1>Antecedent basis checker</h1>
          <p className="patent-tool__note">
            SudachiPy-based noun-phrase extraction and basis checking for references such as 前記 and 該.
          </p>
          <p className="patent-tool__note">
            <Link className="patent-tool__backlink" to="/">
              Back to overview
            </Link>
          </p>
        </header>

        <section className="patent-tool__card">
          <div className="patent-tool__card-head">
            <span className="patent-tool__step">1. Claims input</span>
            <button
              className="patent-entry__submit"
              type="button"
              onClick={runCheck}
              disabled={loading}
            >
              {loading ? 'Analyzing…' : 'Run check'}
            </button>
          </div>
          <textarea
            className="patent-entry__textarea patent-tool__textarea"
            rows="14"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="【請求項１】&#10;照明装置であって、...&#10;&#10;【請求項２】&#10;前記照明装置において..."
          />
          <p className="patent-tool__hint">Supported format: `【請求項N】` with ASCII or full-width digits.</p>
          {error ? <p className="patent-entry__error">{error}</p> : null}
        </section>

        {result ? (
          <section className="patent-tool__card">
            <div className="patent-tool__card-head">
              <span className="patent-tool__step">2. Results</span>
              <span className={missing ? 'patent-tool__summary patent-tool__summary--danger' : 'patent-tool__summary patent-tool__summary--ok'}>
                {`References ${totalRefs}, missing basis ${missing}`}
              </span>
            </div>
            {!totalRefs ? (
              <p className="patent-tool__hint">No 前記 / 該 references were found.</p>
            ) : (
              <div className="patent-tool__table-wrap">
                <table className="patent-tool__table">
                  <thead>
                    <tr>
                      <th>Claim</th>
                      <th>Claim text</th>
                      <th>Reference</th>
                      <th>Basis</th>
                      <th>Found claim</th>
                      <th>Context</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claims.flatMap((claim) => {
                      if (!claim.refs.length) {
                        return [];
                      }
                      const terms = claim.refs.map((ref) => ref.term);
                      const claimHtml = highlightRefs(claim.text, terms);
                      return claim.refs.map((ref, index) => {
                        const chainStrings = (ref.dep_chains || []).map((chain) =>
                          chain.join(' › '),
                        );
                        return (
                          <tr key={`${claim.num}-${index}`} className={index === 0 ? 'patent-tool__group-start' : ''}>
                            {index === 0 ? (
                              <>
                                <td rowSpan={claim.refs.length} className="patent-tool__claim-num">
                                  {`請求項${claim.num}`}
                                </td>
                                <td
                                  rowSpan={claim.refs.length}
                                  className="patent-tool__claim-text"
                                  dangerouslySetInnerHTML={{ __html: claimHtml }}
                                />
                              </>
                            ) : null}
                            <td>
                              <code>{ref.term}</code>
                            </td>
                            <td>
                              {ref.self_found ? (
                                <span className="patent-tool__badge patent-tool__badge--self">Same claim</span>
                              ) : ref.preceding_nums.length ? (
                                <span className="patent-tool__badge patent-tool__badge--ok">Found</span>
                              ) : (
                                <span className="patent-tool__badge patent-tool__badge--danger">Missing</span>
                              )}
                            </td>
                            <td className="patent-tool__small">
                              {chainStrings.length ? chainStrings.join('\n') : ref.self_found ? '' : '—'}
                            </td>
                            <td>
                              {ref.contexts.length ? (
                                ref.contexts.map((context, contextIndex) => (
                                  <div
                                    key={`${claim.num}-${index}-${contextIndex}`}
                                    className={`patent-tool__context${context.label.includes('同一') ? ' patent-tool__context--self' : ''}`}
                                  >
                                    <span className="patent-tool__context-label">{context.label}</span>
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          '…' +
                                          highlightSnippet(
                                            context.snippet,
                                            context.term_start,
                                            context.term_len,
                                          ) +
                                          '…',
                                      }}
                                    />
                                  </div>
                                ))
                              ) : (
                                <span className="patent-tool__hint">No antecedent found.</span>
                              )}
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ) : null}
      </section>
    </main>
  );
}
